import { RefObject, useLayoutEffect, useMemo, useRef, useState } from 'react';

import {
  useBufferPool,
} from '@features/analysis/rendering/Panels/Renderers/vtkManagers';
import { usePanelContainer } from '@features/analysis/rendering/Panels/usePanelContainer';
import { useBrowserPage } from '@features/browser/useBrowserPage';
import { useConfig } from 'config/useConfig';
import useModalActions from '@hooks/common/useModalAction';

import { SavedInfo } from '@models/Parameter';
import D3Table from '@components/SVGTable/SVGTable';
import { configStore } from 'config/configStore';
import { PanelComponentKeyType } from 'config/panelConfig';

type renderArgs = {
   componentKey?:PanelComponentKeyType;
   jsonRef?: RefObject<ArrayBuffer | undefined>;
};

export function usePanelOverlayText({
  componentKey,
  jsonRef,
}: renderArgs) {
  const { selectedSeriesId, selectedSeries } = useBrowserPage();
  const {
    analysisType,
  } = usePanelContainer();
  const { bufferPool, setBufferPool } = useBufferPool();
  const pool = selectedSeriesId ? bufferPool[selectedSeriesId] : undefined;
  const { config, getColors } = useConfig();
  const rrTextRef = useRef<HTMLDivElement>(null);
  const [rrTrackWidth, setRrTrackWidth] = useState<number>(0);
  const showRRText = componentKey !== 'Direction';

  const percentRR = analysisType === 'ejectionFraction'? config.rrPercent_ef : config.rrPercent;
  useLayoutEffect(() => {
    if (!showRRText) return;
    const target = rrTextRef.current;
    if (!target) return;
    const nextWidth = Math.ceil(target.getBoundingClientRect().width);
    setRrTrackWidth((prev) => (prev === nextWidth ? prev : nextWidth));
  }, []);


  const topContent = useMemo(() => {
    const headers = ['Component', 'Volume'];
    if(!pool?.parameter?.data) return;
    const raw = pool?.parameter?.data as any;
    const volumeData: SavedInfo | undefined = raw?.SavedInfo;
    const safeFixed = (val?: number, unit?: string) =>
      typeof val === 'number' ? `${val.toFixed(1)}${unit ?? ''}` : '-';

    const data = [
      {
        Component: 'Epicardial Fat',
        Volume: safeFixed(volumeData?.Volumes?.epicardial, ' mL'),
      },
    ];
    return <D3Table data={data} headers={headers} />;
  }, [analysisType]);


  const topLeftContentForCAC = useMemo(() => {
    //pool?.parameter은 상위 클래스에서 먼저 다운받아서 config에 때려 넣으니깐 값이 있다는 과정.

    if (analysisType === 'calciumScore') {
      const calciumScore = config.agatstonScore;
      const headers = ['Artery', 'Volume (mm³)', 'Score'];
      const data = [
        {
          Artery: 'LM',
          'Volume (mm³)': calciumScore[6].toFixed(1),
          Score: calciumScore[1].toFixed(1),
        },
        {
          Artery: 'LAD',
          'Volume (mm³)': calciumScore[7].toFixed(1),
          Score: calciumScore[2].toFixed(1),
        },
        {
          Artery: 'LCX',
          'Volume (mm³)': calciumScore[8].toFixed(1),
          Score: calciumScore[3].toFixed(1),
        },
        {
          Artery: 'RCA',
          'Volume (mm³)': calciumScore[9].toFixed(1),
          Score: calciumScore[4].toFixed(1),
        },
        {
          Artery: 'Total',
          'Volume (mm³)': calciumScore[5].toFixed(1),
          Score: calciumScore[0].toFixed(1),
        },
      ];
      return <D3Table data={data} headers={headers} />;
    }

    if (analysisType === 'aorticValveCalcium') {
      const headers = ['Valve', 'Volume (mm³)', 'Score'];
      const data = [
        {
          Valve: 'Aortic Valve',
          'Volume (mm³)': config.avcCalciumVolume,
          Score: config.avcCalciumScore.toFixed(1),
        },
      ];
      return <D3Table data={data} headers={headers} />;
    }

    if (analysisType === 'segmentation') {
      const headers = ['Component', 'Chamber', 'Wall'];
      const raw = pool?.parameter?.data as any;
      const volumeData: SavedInfo | undefined = raw?.SavedInfo;
      const safeFixed = (val?: number, unit?: string) =>
        typeof val === 'number' ? `${val.toFixed(1)}${unit ?? ''}` : '-';

      const data = [
        {
          Component: 'Aorta',
          Chamber: safeFixed(volumeData?.Volumes?.aorta, ' mL'),
          Wall: '-',
        },
        {
          Component: 'LA',
          Chamber: safeFixed(volumeData?.Volumes?.la, ' mL'),
          Wall: safeFixed(volumeData?.Volumes?.la_wall, ' mL'),
        },
        {
          Component: 'LV',
          Chamber: safeFixed(volumeData?.Volumes?.lv, ' mL'),
          Wall: safeFixed(volumeData?.Volumes?.lv_wall, ' mL'),
        },
        {
          Component: 'RA',
          Chamber: safeFixed(volumeData?.Volumes?.ra, ' mL'),
          Wall: safeFixed(volumeData?.Volumes?.ra_wall, ' mL'),
        },
        {
          Component: 'RV',
          Chamber: safeFixed(volumeData?.Volumes?.rv, ' mL'),
          Wall: safeFixed(volumeData?.Volumes?.rv_wall, ' mL'),
        },
      ];
      return <D3Table data={data} headers={headers} />;
    }
    if (analysisType === 'myocardial') {
      if (!jsonRef?.current) return;
      return (
        <>
          <div className="plain-text">
            Myocardial mass at risk : {config.risk.volume} mm³ /{' '}
            {config.risk.lvMass.mass} g ({config.risk.lvMass.pct}%) /{' '}
            {config.risk.vmRatio} mm³/g
          </div>

          <div className="plain-text">
            Total : {config.total.volume} mm³ / {config.total.lvMass} g /{' '}
            {config.total.vmRatio} mm³/g
          </div>

          <div className="plain-text">
            RCA : {config.dist.rca.mass} g ({config.dist.rca.pct}%)
          </div>

          <div className="plain-text">
            LAD : {config.dist.lad.mass} g ({config.dist.lad.pct}%)
          </div>

          <div className="plain-text">
            LCX : {config.dist.lcx.mass} g ({config.dist.lcx.pct}%)
          </div>
        </>
      );
    }
    return null;
  }, [analysisType, jsonRef?.current]);


  const midLeftContentRRPercent = useMemo(() => {
    if (!showRRText || percentRR === undefined) return null;
    const firstPeakX = 20;
    const secondPeakX = 80;
    const baseY = 20;
    const offsetY = baseY + 7;

    const generatePeak = (centerX: number) => [
      [centerX - 5, baseY],
      [centerX - 2, offsetY],
      [centerX, 0],
      [centerX + 2, offsetY],
      [centerX + 5, baseY],
    ];

    const ecgPointsArray = [
      [0, baseY],
      [10, baseY],
      ...generatePeak(firstPeakX),
      [30, baseY],
      [60, baseY],
      ...generatePeak(secondPeakX),
      [90, baseY],
      [100, baseY],
    ];

    const ecgPoints = ecgPointsArray
      .map(([x, y]) => `${x},${y}`)
      .join(' ');

    const markerX = firstPeakX + ((secondPeakX - firstPeakX) * percentRR) / 100;
    return (
      <div className="rr-percent-overlay">
        <div ref={rrTextRef} className="plain-text rr-percent-text">
          %R - R : {percentRR}
        </div>
        <div
          className="rr-percent-track"
         // style={rrTrackWidth > 0 ? { width: `${rrTrackWidth}px` } : undefined}
        >
          <svg
            className="rr-percent-ecg"
            viewBox="0 0 100 27"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
          <polyline points={ecgPoints} />
            <line
              className="rr-percent-marker-line"
              x1={markerX}
              x2={markerX}
              y1="0"
              y2="31"
            />
          </svg>
        </div>
      </div>
    );
  }, [percentRR, rrTrackWidth]);

  return {
    topContent,
    topLeftContentForCAC,
    midLeftContentRRPercent,
  }
}
